import React, { useState, useEffect } from 'react';
import { Shield, Upload, Plus, X, Award, Globe, MapPin, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import PasscodeInput from '../components/PasscodeInput';
import PhoneInput from '../components/PhoneInput';
import ImageUploadWithCrop from '../components/ImageUploadWithCrop';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import { toast } from 'sonner';

const SPECIALIZATIONS = [
  'Residential', 'Commercial', 'Luxury Homes', 'Investment Properties',
  'First-Time Buyers', 'Relocation', 'New Construction', 'Foreclosures'
];

const LANGUAGES = [
  'English', 'Spanish', 'Mandarin', 'French', 'German', 'Italian',
  'Portuguese', 'Japanese', 'Korean', 'Hindi', 'Arabic'
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function PublicProfile() {
  const [agent, setAgent] = useState(null);
  const [profile, setProfile] = useState({
    photo_url: '',
    email: '',
    phone: '',
    title: '',
    agency_name: '',
    years_of_experience: '',
    website: '',
    social_media: {
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      youtube: ''
    },
    external_reviews_url: '',
    bio: '',
    specializations: [],
    languages: [],
    certifications: [],
    service_areas: [],
    properties_sold: ''
  });
  const [stats, setStats] = useState({
    open_houses_hosted: 0,
    average_rating: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [hasPasscode, setHasPasscode] = useState(false);

  // New item inputs
  const [newCertification, setNewCertification] = useState('');
  const [newServiceArea, setNewServiceArea] = useState({ city: '', state: 'NJ' });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch agent data to check if passcode is set
      const agentResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (agentResponse.ok) {
        const agentData = await agentResponse.json();
        setHasPasscode(agentData.has_passcode);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/public-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const profileData = data.profile || {};
        
        setProfile({
          photo_url: profileData.photo_url || '',
          email: profileData.email || data.email || '',
          phone: profileData.phone || data.phone || '',
          title: profileData.title || '',
          agency_name: profileData.agency_name || data.office_address || '',
          years_of_experience: profileData.years_of_experience || '',
          website: profileData.website || '',
          social_media: profileData.social_media || {
            facebook: '', instagram: '', linkedin: '', twitter: '', youtube: ''
          },
          bio: profileData.bio || '',
          specializations: profileData.specializations || [],
          languages: profileData.languages || [],
          certifications: profileData.certifications || [],
          service_areas: profileData.service_areas || [],
          properties_sold: profileData.properties_sold || '',
          external_reviews_url: profileData.external_reviews_url || ''
        });
        
        setStats({
          open_houses_hosted: profileData.open_houses_hosted || 0
        });

        setAgent({
          id: data.id || data.agent_id,
          role: data.role || 'agent',
          name: data.name,
          email: data.email,
          phone: data.phone,
          profile_slug: data.profile_slug
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handlePasscodeSubmit = async () => {
    if (passcode.length !== 6) {
      toast.error('Please enter your 6-digit passcode');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/passcode/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ passcode })
      });

      if (response.ok) {
        setIsEditing(true);
        setShowPasscodeDialog(false);
        setPasscode('');
        toast.success('Passcode verified! You can now edit your profile.');
      } else {
        toast.error('Invalid passcode');
      }
    } catch (error) {
      console.error('Error verifying passcode:', error);
      toast.error('Failed to verify passcode');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    fetchProfile(); // Reset to original data
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Convert years_of_experience and properties_sold to numbers
      const payload = {
        ...profile,
        years_of_experience: profile.years_of_experience ? parseInt(profile.years_of_experience) : null,
        properties_sold: profile.properties_sold ? parseInt(profile.properties_sold) : null
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/public-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Public profile updated successfully!');
        setIsEditing(false);
        fetchProfile(); // Refresh to get updated stats
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleSpecialization = (spec) => {
    setProfile(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const toggleLanguage = (lang) => {
    setProfile(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setProfile(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (index) => {
    setProfile(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const addServiceArea = () => {
    if (newServiceArea.city.trim() && newServiceArea.state) {
      const titleCity = newServiceArea.city.trim().replace(/\b\w/g, c => c.toUpperCase());
      setProfile(prev => ({
        ...prev,
        service_areas: [...prev.service_areas, { city: titleCity, state: newServiceArea.state }]
      }));
      setNewServiceArea({ city: '', state: 'NJ' });
    } else {
      toast.error('Please enter both city and state');
    }
  };

  const removeServiceArea = (index) => {
    setProfile(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]">Public Profile</h1>
          <p className="text-stone-600 mt-1">Manage your professional profile visible to the public</p>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <Button 
              onClick={handleEditClick}
              className="bg-[#1A4D2E] hover:bg-[#2d7a4a]"
            >
              Edit Profile
            </Button>
          ) : (
            <>
              <Button 
                onClick={handleCancelEdit}
                variant="outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#1A4D2E] hover:bg-[#2d7a4a]"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Passcode Dialog */}
      <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <Lock size={24} />
              Enter Your Passcode
            </DialogTitle>
            <DialogDescription>
              Enter your security passcode to edit your public profile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <PasscodeInput
                length={6}
                value={passcode}
                onChange={setPasscode}
                autoFocus
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowPasscodeDialog(false);
                setPasscode('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasscodeSubmit}
              disabled={passcode.length !== 6}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#15381F]"
            >
              Verify
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]">{stats.open_houses_hosted}</div>
            <p className="text-sm text-stone-600">Open Houses Hosted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]">{profile.properties_sold || 0}</div>
            <p className="text-sm text-stone-600">Properties Sold</p>
          </CardContent>
        </Card>
      </div>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>Your professional details and contact information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Profile Photo Upload with Crop - Oval shape for Agent/Broker */}
          <ImageUploadWithCrop
            currentImage={profile.photo_url}
            onImageChange={(imageData) => setProfile({...profile, photo_url: imageData})}
            shape="oval"
            disabled={!isEditing}
            label="Profile Photo"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={profile.email}
                disabled
                placeholder="Not provided"
                className="bg-stone-100"
              />
              <p className="text-xs text-stone-500 mt-1">Email cannot be changed from public profile</p>
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                type="tel"
                value={formatPhoneDisplay(profile.phone)}
                disabled
                placeholder="Not provided"
                className="bg-stone-100"
              />
              <p className="text-xs text-stone-500 mt-1">Phone cannot be changed from public profile</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input
                value={profile.title}
                onChange={(e) => setProfile({...profile, title: e.target.value})}
                placeholder="e.g., Real Estate Agent, Broker Associate"
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label>Agency / Brokerage Name</Label>
              <Input
                value={profile.agency_name}
                onChange={(e) => setProfile({...profile, agency_name: e.target.value})}
                placeholder="e.g., Keller Williams Realty"
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Years of Experience</Label>
              <Input
                type="number"
                value={profile.years_of_experience}
                onChange={(e) => setProfile({...profile, years_of_experience: e.target.value})}
                placeholder="e.g., 10"
                disabled={!isEditing}
              />
            </div>
            <div>
              <Label>Properties Sold (Manual Entry)</Label>
              <Input
                type="number"
                value={profile.properties_sold}
                onChange={(e) => setProfile({...profile, properties_sold: e.target.value})}
                placeholder="e.g., 150"
                disabled={!isEditing}
              />
            </div>
          </div>

          <div>
            <Label>Website</Label>
            <Input
              value={profile.website}
              onChange={(e) => setProfile({...profile, website: e.target.value})}
              placeholder="https://yourwebsite.com"
              disabled={!isEditing}
            />
          </div>

          {/* Social Media Links */}
          <div className="space-y-2">
            <Label>Social Media Links</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-stone-600">Facebook</Label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                    facebook.com/
                  </span>
                  <Input
                    value={profile.social_media.facebook}
                    onChange={(e) => setProfile({
                      ...profile,
                      social_media: {...profile.social_media, facebook: e.target.value}
                    })}
                    placeholder="username"
                    disabled={!isEditing}
                    className="rounded-l-none"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-stone-600">Instagram</Label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                    instagram.com/
                  </span>
                  <Input
                    value={profile.social_media.instagram}
                    onChange={(e) => setProfile({
                      ...profile,
                      social_media: {...profile.social_media, instagram: e.target.value}
                    })}
                    placeholder="username"
                    disabled={!isEditing}
                    className="rounded-l-none"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-stone-600">LinkedIn</Label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                    linkedin.com/in/
                  </span>
                  <Input
                    value={profile.social_media.linkedin}
                    onChange={(e) => setProfile({
                      ...profile,
                      social_media: {...profile.social_media, linkedin: e.target.value}
                    })}
                    placeholder="username"
                    disabled={!isEditing}
                    className="rounded-l-none"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-stone-600">Twitter (X)</Label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                    twitter.com/
                  </span>
                  <Input
                    value={profile.social_media.twitter}
                    onChange={(e) => setProfile({
                      ...profile,
                      social_media: {...profile.social_media, twitter: e.target.value}
                    })}
                    placeholder="username"
                    disabled={!isEditing}
                    className="rounded-l-none"
                  />
                </div>
              </div>
              
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs text-stone-600">YouTube</Label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md text-sm text-stone-600">
                    youtube.com/@
                  </span>
                  <Input
                    value={profile.social_media.youtube}
                    onChange={(e) => setProfile({
                      ...profile,
                      social_media: {...profile.social_media, youtube: e.target.value}
                    })}
                    placeholder="channelname"
                    disabled={!isEditing}
                    className="rounded-l-none"
                  />
                </div>
              </div>

              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs text-stone-600">External Reviews</Label>
                <div className="flex gap-2">
                  <Input
                    value={profile.external_reviews_url || ''}
                    onChange={(e) => setProfile({...profile, external_reviews_url: e.target.value})}
                    placeholder="https://g.page/your-business/review or any review link"
                    disabled={!isEditing}
                    className="flex-1"
                  />
                  {!isEditing && profile.external_reviews_url && (
                    <a href={profile.external_reviews_url.startsWith('http') ? profile.external_reviews_url : `https://${profile.external_reviews_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors whitespace-nowrap" data-testid="external-reviews-link">
                      Open Link
                    </a>
                  )}
                </div>
                <p className="text-xs text-stone-400">Google Reviews, Yelp, Zillow, or any external review page URL</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About Agent */}
      <Card>
        <CardHeader>
          <CardTitle>About Me</CardTitle>
          <CardDescription>Tell clients about yourself and your expertise</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Professional Bio</Label>
            <Textarea
              value={profile.bio}
              onChange={(e) => setProfile({...profile, bio: e.target.value})}
              placeholder="Write a compelling professional bio..."
              rows={6}
              disabled={!isEditing}
            />
            <p className="text-xs text-stone-500 mt-1">
              {profile.bio.length}/1000 characters
            </p>
          </div>

          <div>
            <Label>Specializations</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SPECIALIZATIONS.map(spec => (
                <button
                  key={spec}
                  onClick={() => isEditing && toggleSpecialization(spec)}
                  disabled={!isEditing}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    profile.specializations.includes(spec)
                      ? 'bg-[#1A4D2E] text-white'
                      : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                  } ${!isEditing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Languages Spoken</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {LANGUAGES.map(lang => (
                <button
                  key={lang}
                  onClick={() => isEditing && toggleLanguage(lang)}
                  disabled={!isEditing}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    profile.languages.includes(lang)
                      ? 'bg-blue-600 text-white'
                      : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                  } ${!isEditing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Certifications & Awards</Label>
            {isEditing && (
              <div className="flex gap-2 mt-2">
                <Input
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  placeholder="e.g., ABR, CRS, GRI"
                  onKeyPress={(e) => e.key === 'Enter' && addCertification()}
                />
                <Button onClick={addCertification} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              {profile.certifications.map((cert, idx) => (
                <div key={idx} className="flex items-center gap-1 bg-amber-100 text-amber-800 px-3 py-1 rounded-full">
                  <Award size={14} />
                  <span className="text-sm">{cert}</span>
                  {isEditing && (
                    <button onClick={() => removeCertification(idx)} className="ml-1">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Areas */}
      <Card>
        <CardHeader>
          <CardTitle>Service Areas</CardTitle>
          <CardDescription>Cities and states where you provide services</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditing && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={newServiceArea.city}
                onChange={(e) => setNewServiceArea({...newServiceArea, city: e.target.value})}
                placeholder="City"
                className="w-full sm:flex-1"
              />
              <div className="flex gap-2">
                <Select value={newServiceArea.state} onValueChange={(val) => setNewServiceArea({...newServiceArea, state: val})}>
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addServiceArea} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {profile.service_areas.map((area, idx) => (
              <div key={idx} className="flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full">
                <MapPin size={14} />
                <span className="text-sm">{area.city}, {area.state}</span>
                {isEditing && (
                  <button onClick={() => removeServiceArea(idx)} className="ml-1">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
